<template>
  <MainLayout :hide-route-header="true">
    <ContactNotFound v-if="hasError" />

    <template v-else-if="contact">
      <div class="section-width space-y-6">
        <div class="hidden lg:flex lg:items-center lg:justify-between lg:gap-6">
          <PersonHeader :person="contact">
            <template #badges>
              <ContactStageBadge :stage="contact.stage" />
              <CandidateLicenseBadge :license-status-label="contact.licenseStatus" />
              <ContactLeadSource :contact="contact" />
              <ContactReferredByBadge :referred-by="contact.referredBy" />
            </template>
          </PersonHeader>
          <ContactActionBar class="shrink-0 justify-end" :contact="contact" @reload="reloadAllData" />
        </div>

        <div class="lg:hidden">
          <PersonHeader :person="contact">
            <template #badges>
              <ContactStageBadge :stage="contact.stage" />
              <CandidateLicenseBadge :license-status-label="contact.licenseStatus" />
              <ContactLeadSource :contact="contact" />
              <ContactReferredByBadge :referred-by="contact.referredBy" />
            </template>
          </PersonHeader>
          <ContactActionBar class="mt-6 flex justify-start" :contact="contact" @reload="reloadAllData" />
        </div>

        <LiveContactEventAlert :events="contact.interviews ?? []" />

        <div class="grid grid-cols-1 gap-6 lg:grid-flow-col-dense lg:grid-cols-3">
          <div class="space-y-6 lg:col-start-1 lg:col-span-2">
            <PersonCustomFields
              :person="contact"
              :custom-fields-list="customFieldsList"
              @custom-fields-updated="contactQuery.reload"
            />
            <Card
              v-if="Object.keys(contact.customAttributes).length > 0"
              extra-classes="mt-6 overflow-hidden sm:rounded-lg"
            >
              <CardContent>
                <div class="flex flex-wrap justify-between border-b border-gray-200 px-4 py-5 sm:px-6">
                  <div>
                    <h3 class="inline-block text-lg leading-6 font-medium text-gray-900">Custom fields</h3>
                  </div>
                </div>
                <div class="px-4 py-5 sm:px-6">
                  <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-4">
                    <div v-for="key in Object.keys(contact.customAttributes)" :key="key" class="sm:col-span-1">
                      <dt class="text-sm font-medium leading-5 text-gray-500">
                        {{ key }}
                      </dt>
                      <dd class="mt-1 text-sm leading-5 text-gray-900">
                        {{ contact.customAttributes[key] ? contact.customAttributes[key] : 'null' }}
                      </dd>
                    </div>
                  </dl>
                </div>
              </CardContent>
            </Card>

            <section aria-labelledby="notes-title">
              <ContactUpcoming
                :contact="contact"
                :activities="contact.activities.future"
                @data-changed="reloadAllData"
              />
              <ContactActivity
                :contact="contact"
                :activities="contact.activities.past"
                @activity-created="contactQuery.reload"
                @activity-deleted="contactQuery.reload"
              />
            </section>
          </div>

          <div>
            <section v-if="contact.stage === 'Disqualified'" aria-labelledby="reapply-title" class="mb-6">
              <ContactReapplyToggle :contact="contact" @update="updateReapplyAllowed" />
            </section>
            <section aria-labelledby="task-overview-title" class="mb-6">
              <TaskOverviewCard :contact-id="contactId" :tasks-query="tasksQuery" />
            </section>
            <section v-if="contact.applications.length > 1" aria-labelledby="applications-title" class="mb-6">
              <ContactApplications :applications="contact.applications" />
            </section>
            <section aria-labelledby="attachments-title" class="mb-6 lg:col-start-3 lg:col-span-1">
              <Attachments :contact-id="contact.id" :files="contact.files" @reload="reloadAllData" />
            </section>
            <section aria-labelledby="enrichment-title" class="mt-6">
              <ContactEnrichment :contact="contact" />
            </section>
          </div>
        </div>
      </div>

      <section
        v-if="route.query.history"
        aria-labelledby="history-title"
        class="mt-6 mx-auto grid max-w-3xl grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-1"
      >
        <History :contact-id="contactId" />
      </section>
    </template>

    <div v-else class="flex justify-center py-32">
      <div class="spinner"></div>
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */

import { computed } from 'vue';
import { useRoute } from 'vue-router';
import MainLayout from '@/components/layouts/MainLayout/index.vue';
import ContactActivity from './ContactActivity/index.vue';
import ContactApplications from './ContactApplications.vue';
import History from '../person/History.vue';
import ContactEnrichment from './ContactEnrichment.vue';
import ContactUpcoming from './ContactUpcoming.vue';
import TaskOverviewCard from '../person/TaskOverviewCard/index.vue';
import { Card, CardContent } from '@/components/ui/card';
import ContactReapplyToggle from './ContactReapplyToggle/index.vue';
import { useReapplyToggle } from './ContactReapplyToggle/useReapplyToggle';
import DateUtility from '../../utilities/dates';
import { Api } from '@/api';
import Attachments from '../person/Attachments.vue';
import RailsUtility from '@/utilities/rails';
import type { ContactDetail } from './ContactDetail';
import type { CustomFieldEntity } from '@/api/entity_types/CustomField';
import PersonHeader from '@/components/person/PersonHeader/index.vue';
import ContactStageBadge from './ContactStageBadge.vue';
import CandidateLicenseBadge from '@/components/recruiting/Candidates/CandidateLicenseBadge.vue';
import ContactLeadSource from './ContactLeadSource.vue';
import ContactReferredByBadge from './ContactReferredByBadge.vue';
import ContactActionBar from './ContactActionBar.vue';
import type { LicenseStatusLabel } from '@/types/LicenseStatusLabel';
import { useActivityTransformer } from './ContactActivity/Transformers/useActivityTransformer';
import PersonCustomFields from '@/components/person/PersonCustomFields/index.vue';
import ContactNotFound from './ContactNotFound.vue';
import LiveContactEventAlert from '@/components/shared/LiveContactEventAlert.vue';
import { useHFetchQuery } from '@/lib/hfetch/useHFetchQuery';

const route = useRoute();
const activityTransformer = useActivityTransformer();

const contactId = computed(() => route.params.id as string);

const contactQuery = useHFetchQuery(async () => {
  const data = await Api.Contacts.getContact(contactId.value);
  if (!data) return { status: 'error', messages: ['Contact not found'] };
  return {
    status: 'success',
    data: {
      contact: buildContactDetail(data),
      customFieldsList: data.custom_fields_for_entity?.sort(sortCustomFields) ?? [],
    },
  };
});

const tasksQuery = useHFetchQuery(() => Api.Contacts.getContactTasks({ id: +contactId.value }));

const contact = computed(() => contactQuery.data.value?.contact ?? null);
const customFieldsList = computed(() => contactQuery.data.value?.customFieldsList ?? []);
const hasError = computed(() => !!contactQuery.errors.value);

const { updateReapplyAllowed } = useReapplyToggle(contact);

const reloadAllData = async () => {
  await contactQuery.reload();
  await tasksQuery.reload();
};

const buildContactDetail = (data: any): ContactDetail => ({
  id: data.record.id,
  name: data.record.name,
  firstName: data.record.first_name,
  lastName: data.record.last_name,
  email: data.record.email,
  phone: data.record.phone_number,
  status: data.record.status,
  recordType: data.record.record_type,
  stage: data.record.stage,
  reapplyAllowed: data.record.reapply_allowed,
  licenseStatus: RailsUtility.convertFromCasedEnumFormat(data.record.license_status) as LicenseStatusLabel,
  license: data.record.re_license_number,
  customAttributes: data.record.jsonb,
  created: formateDate(data.record.created_at) ?? '',
  phoneScreenTime: formateDate(data.record.phone_screen_at),
  interviewTime: formateDate(data.record.interview_at),
  icaAgreementStatus: data.record.ica_agreement_status,
  source: data.record.source,
  attributionParams: data.record.attribution_params,
  applications: data.applications ?? [],
  customFields: data.custom_field_values || [],
  activities:
    activityTransformer.buildActivities({
      rawActivities: data.activities,
      applications: data.applications ?? [],
      contact: data.record,
      getContactDetails: contactQuery.reload,
    }) || [],
  canReceiveSms: data.record['can_receive_sms?'] ?? true,
  interviews: data.activities.contact_events ?? [],
  files: data.files,
  referredBy: data.referred_by ?? null,
});

const formateDate = (val: string | undefined) => {
  if (!val) return null;
  return DateUtility.formatDate(val, 'MMMM dd yyyy, h:mm a');
};

const sortCustomFields = (a: CustomFieldEntity, b: CustomFieldEntity) => a.name.localeCompare(b.name);
</script>

<style scoped>
@reference "tailwindcss";
.section-width {
  @apply mx-auto max-w-3xl lg:max-w-7xl;
}
</style>
